import cn from 'classnames';
import sortBy from 'lodash.sortby';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import AppModulePill from './AppModulePill';
import PreviewModal from './PreviewModal';
import SKUModal from './SKUModal';

const ConfigurationForm = ({
	externalService,
	exServData,
	onFormSubmit,
	requiredServiceForms,
}) => {
	const selectedForms = externalService.serviceForms.map(
		({ care_quest_group_id }) => care_quest_group_id,
	);
	const eRefAnswerId = exServData?.tags
		.find((t) => t.category === 'accepts_e_ref')
		?.tag_answers?.find((ta) => ta.name === 'accepts_e_ref_currently')?.id;
	const defaultServiceForms = [];
	requiredServiceForms.forEach((service) => {
		defaultServiceForms.push({
			...service,
			value: selectedForms.includes(service.id),
		});
	});

	const defaultValues = {
		serviceForms: defaultServiceForms,
		private_data: externalService.private_data,
		availability: externalService.availability,
		waitlist_ind: externalService.waitlist_ind,
		allow_self_ref_ind: externalService.allow_self_ref_ind,
		serviceAtLocationId: exServData.id,
		eRefAnswerId,
		existingAvailability: externalService.availability,
	};

	const { register, handleSubmit, control, watch, setValue } = useForm({
		defaultValues,
	});
	const { fields } = useFieldArray({ control, name: 'serviceForms' });
	const navigate = useNavigate();
	const [isPreviewOpen, setPreviewOpen] = useState(false);
	const [previewFormId, setPreviewFormId] = useState();
	const [isSKUFormModalOpen, setSKUFormModalOpen] = useState(false);
	const [selectedServiceName, setSelectedServiceName] = useState('');

	const handleOpenPreview = (formId) => {
		setPreviewFormId(formId);
		setPreviewOpen(true);
	};

	const availabilityValue = watch('availability');
	const watchedFields = watch('serviceForms');
	const serviceFields = fields.map((field, index) => ({
		...field,
		...watchedFields[index],
	}));
	let fieldIndex = 0;

	const handleAddModule = ({ target }) => {
		setValue(`serviceForms.${target.value}.value`, true);
	};

	const handleOpenSKUModal = (event) => {
		event.preventDefault();
		setSelectedServiceName(event.target.dataset.serviceName);
		setSKUFormModalOpen(true);
	};

	return (
		<>
			<form onSubmit={handleSubmit(onFormSubmit)}>
				<h2>{exServData.name} - Configuration</h2>
				<div className="divider" />
				<div className="grid grid-cols-4 gap-4">
					<div className="self-center">
						<strong>Category</strong>
					</div>
					<div className="flex flex-row self-center col-span-3">
						<div className="self-center flex-1">{exServData.name}</div>
						<button
							className="btn btn-sm btn-primary"
							data-service-name={exServData.name}
							onClick={handleOpenSKUModal}
						>
							Edit SKUs
						</button>
					</div>
					<label htmlFor="availability" className="self-center">
						<strong>Accepting Electronic Referrals</strong>
					</label>
					<div className="col-span-3">
						<select
							id="availability"
							className="mr-4 select select-sm select-bordered"
							{...register('availability')}
						>
							<option value={1}>Yes</option>
							<option value={0}>No</option>
						</select>
					</div>
					<label htmlFor="waitlist_ind" className="self-center">
						<strong>Place referrals on waitlist</strong>
					</label>
					<div className="col-span-3">
						<select
							id="waitlist_ind"
							className="select select-sm select-bordered"
							{...register('waitlist_ind')}
							disabled={availabilityValue == 0}
						>
							<option value={1}>Yes</option>
							<option value={0}>No</option>
						</select>
					</div>
					<label htmlFor="allow_self_ref_ind" className="self-center">
						<strong>Allow Self-referrals</strong>
					</label>
					<div className="col-span-3">
						<select
							id="allow_self_ref_ind"
							className="select select-sm select-bordered"
							{...register('allow_self_ref_ind')}
							disabled={availabilityValue == 0}
						>
							<option value={1}>Yes</option>
							<option value={0}>No</option>
						</select>
					</div>
					<label htmlFor="add-module-select" className="self-center">
						<strong>Application Modules</strong>
					</label>
					<div className="col-span-3 form-control">
						<select
							className="select select-sm select-bordered"
							onChange={handleAddModule}
							defaultValue="Add Module..."
							id="add-module-select"
							disabled={availabilityValue == 0}
						>
							<option disabled>Add Module...</option>
							{serviceFields
								.map((field, index) => ({
									...field,
									fieldIndex: index,
								}))
								.map((field) => (
									<option
										key={`${field.id}`}
										value={field.fieldIndex}
										disabled={field.value}
									>
										{field.name}
									</option>
								))}
						</select>
					</div>
					<div className="col-span-3 col-start-2">
						{serviceFields.map((field) => (
							<AppModulePill
								key={field.id}
								field={field}
								fieldIndex={fieldIndex++}
								register={register}
								onPreview={handleOpenPreview}
							/>
						))}
					</div>
				</div>
				<div className={cn({ hidden: availabilityValue == 0 })}>
					<div className="mt-4 mb-2">
						<strong>Service(s)</strong>
					</div>
					<div>
						{sortBy(exServData.service_taxonomy, ['taxonomy_detail'])
							.map(({ taxonomy_detail }) => taxonomy_detail)
							.join(', ')}
					</div>
				</div>
				<div className="divider" />
				<div className="flex justify-end">
					<input
						type="button"
						className="mr-4 btn btn-outline btn-primary btn-wide"
						onClick={() => navigate(-1)}
						value="Cancel"
					/>
					<button className="btn btn-primary btn-wide" type="submit">
						Save
					</button>
				</div>
			</form>
			<PreviewModal
				isOpen={isPreviewOpen}
				onClose={() => setPreviewOpen(false)}
				formId={previewFormId}
			/>
			<SKUModal
				isOpen={isSKUFormModalOpen}
				onCancel={() => setSKUFormModalOpen(false)}
				serviceName={selectedServiceName}
				exServiceId={externalService.id}
			/>
		</>
	);
};

ConfigurationForm.propTypes = {
	externalService: PropTypes.object.isRequired,
	exServData: PropTypes.object.isRequired,
	requiredServiceForms: PropTypes.array.isRequired,
	onFormSubmit: PropTypes.func.isRequired,
};

export default ConfigurationForm;
